// src/components/WalletSecurity/WalletCard.jsx
// Tarjeta de wallet estilo OpenSea para el perfil PROPIO (privado — solo el dueño la ve):
// identicon de gradiente + dirección + copiar + red Base + saldos USDC/USDT (on-chain) +
// link al explorador. Los saldos NUNCA se muestran en el perfil público de trader.
import React, { useState, useEffect, useRef } from 'react';
import { FiCopy, FiCheck, FiExternalLink } from 'react-icons/fi';
import { useAuth } from '../../Context/AuthContext';
import { seedGradient } from '../common/Identicon';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function Balance({ symbol, logo, value }) {
  return (
    <div style={{ flex: 1, minWidth: 0, background: 'var(--mk-bg-dark, #f3f4f6)', borderRadius: 12, padding: '11px 13px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.72rem', color: 'var(--mk-text-secondary)' }}>
        <img src={logo} alt="" width={16} height={16} style={{ borderRadius: '50%' }} /> {symbol}
      </div>
      <div style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--mk-text-primary)', marginTop: 3, fontFamily: 'var(--fin-font-mono, monospace)', fontVariantNumeric: 'tabular-nums' }}>
        {value == null ? '—' : fmt(value)}
      </div>
    </div>
  );
}

export default function WalletCard() {
  const { user } = useAuth();
  const [tokens, setTokens] = useState(null);
  const [copied, setCopied] = useState(false);
  const mounted = useRef(true);
  const addr = user?.walletAddress;

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    if (!addr) return;
    fetch(`${API_URL}/api/me/wallet-balance`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then((r) => r.json())
      .then((d) => mounted.current && setTokens(d.tokens || []))
      .catch(() => mounted.current && setTokens([]));
  }, [addr]);

  if (!addr) return null;
  const short = `${addr.slice(0, 6)}…${addr.slice(-4)}`;
  const bal = (sym) => tokens?.find((t) => t.symbol === sym)?.balance ?? null;
  const copy = async () => {
    try { await navigator.clipboard.writeText(addr); setCopied(true); setTimeout(() => mounted.current && setCopied(false), 1500); } catch {}
  };

  return (
    <div style={{ background: 'var(--mk-bg-card, #fff)', border: '1px solid var(--mk-border, #e2e8f0)', borderRadius: 16, overflow: 'hidden' }}>
      <div style={{ height: 52, background: 'linear-gradient(135deg, hsl(150 80% 45%), hsl(190 78% 45%))' }} />
      <div style={{ padding: '0 16px 16px', marginTop: -24 }}>
        <div style={{ width: 50, height: 50, borderRadius: '50%', background: seedGradient(addr), border: '3px solid var(--mk-bg-card, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.14)' }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 9 }}>
          <span style={{ fontFamily: 'var(--fin-font-mono, monospace)', fontSize: '0.95rem', fontWeight: 700, color: 'var(--mk-text-primary)' }}>{short}</span>
          <button onClick={copy} title="Copiar dirección" style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: copied ? '#00860c' : 'var(--mk-text-tertiary)', display: 'inline-flex' }}>
            {copied ? <FiCheck size={15} /> : <FiCopy size={15} />}
          </button>
          <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.7rem', fontWeight: 700, color: '#0a7d2e', background: 'rgba(0,212,18,0.12)', padding: '3px 10px', borderRadius: 999 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'currentColor' }} /> Base
          </span>
        </div>
        <div style={{ fontSize: '0.72rem', color: 'var(--mk-text-tertiary)', marginTop: 2 }}>Tu wallet · autocustodia · solo vos ves tu saldo</div>

        <div style={{ display: 'flex', gap: 10, margin: '14px 0 4px' }}>
          <Balance symbol="USDC" logo="/assets/crypto-logos/usdc.svg" value={bal('USDC')} />
          <Balance symbol="USDT" logo="/assets/crypto-logos/usdt.svg" value={bal('USDT')} />
        </div>

        <a href={`https://basescan.org/address/${addr}`} target="_blank" rel="noopener noreferrer"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10, fontSize: '0.78rem', fontWeight: 600, color: 'var(--mk-primary-text, #0a7d2e)', textDecoration: 'none' }}>
          <FiExternalLink size={14} /> Ver en Basescan
        </a>
      </div>
    </div>
  );
}
